import { supabase } from '../../../shared/supabase/supabaseClient';
import type { BranchBarcodeInputMode, BranchScaleBarcodeProfile } from '../../../shell/session/store/sessionTypes';
import type { StockSalePolicy } from '../../../shell/session/settingsRepo';
import { useAuthStore } from '../../store/useAuthStore';

/**
 * Resuelve la organización del usuario como la app/RLS: primero la org activa
 * (`user_active_organization`), fallback la org propia más vieja.
 * Un owner puede tener varias orgs — `.limit(1)` sin order sobre
 * `owner_auth_user_id` devuelve una arbitraria y las queries de sucursales
 * (la RLS solo expone las de la org activa) devuelven vacío.
 */
export async function resolveSessionOrganizationId(userId: string): Promise<string | null> {
  const { data: active } = await supabase
    .from('user_active_organization')
    .select('organization_id')
    .eq('user_id', userId)
    .maybeSingle();
  if (active?.organization_id) {
    return String(active.organization_id);
  }

  const { data: owned, error } = await supabase
    .from('organizations')
    .select('id')
    .eq('owner_auth_user_id', userId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return owned?.id ? String(owned.id) : null;
}

/** Mapper friendly para `apply_branch_settings_command` + manage branches. */
export function mapBranchSettingsCommandError(message: string, fallback: string): string {
  const code = message.trim().toLowerCase();
  if (code.includes('operationid_required')) {
    return 'No se pudo guardar la configuración. Reintentá.';
  }
  if (code.includes('branchid_required') || code.includes('branch_id_required')) {
    return 'Sucursal requerida.';
  }
  if (code.includes('edit_commercial')) {
    return 'No tenés permiso para editar los datos del local.';
  }
  if (code.includes('module_permission_denied') || code.includes('edit_pos_rules')) {
    return 'No tenés permiso para editar las reglas de venta.';
  }
  if (code.includes('open_cash_session')) {
    return 'Hay una caja abierta en esta sucursal. Cerrá el turno antes de desactivarla.';
  }
  if (code.includes('last_active_branch')) {
    return 'No podés desactivar la única sucursal activa.';
  }
  if (code.includes('branch limit reached')) {
    return 'Tu plan no permite más sucursales activas.';
  }
  if (code.includes('branch_not_found')) {
    return 'No encontramos la sucursal.';
  }
  if (code.includes('branch_not_inactive')) {
    return 'La sucursal ya está activa.';
  }
  if (code.includes('owner required')) {
    return 'Solo el dueño puede agregar o desactivar sucursales.';
  }
  if (code.includes('multi_branch not enabled')) {
    return 'Tu plan no incluye más sucursales.';
  }
  if (
    code.includes('failed to fetch') ||
    code.includes('network') ||
    code.includes('offline')
  ) {
    return 'Necesitás conexión para guardar la sucursal.';
  }
  return fallback;
}

/**
 * Error de command de sucursal con el código backend preservado en
 * `backendCode` (regla error-mapping: el código interno no se muestra al
 * usuario pero sirve para clasificar en Sentry — ej. `last_active_branch`
 * es validación esperada, no crash).
 */
function branchCommandError(rawMessage: string, fallback: string): Error {
  const err = new Error(mapBranchSettingsCommandError(rawMessage, fallback)) as Error & {
    backendCode?: string;
  };
  if (rawMessage.trim()) err.backendCode = rawMessage;
  return err;
}

export type BranchIdentityUpdate = {
  name: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

/** Escritura online (sin cola de sync). */
export async function applyBranchSettingsCommand(payload: Record<string, unknown>): Promise<void> {
  const branchId = String(payload.branchId ?? '').trim();
  if (!branchId) throw new Error('Sucursal requerida.');
  const updatedAt =
    typeof payload.updatedAt === 'string' && payload.updatedAt.trim()
      ? payload.updatedAt.trim()
      : new Date().toISOString();
  const operationId =
    typeof payload.operationId === 'string' && payload.operationId.trim()
      ? payload.operationId.trim()
      : `${branchId}:${updatedAt}`;

  const { error } = await supabase.rpc('apply_branch_settings_command', {
    p_operation: 'update',
    p_payload: {
      ...payload,
      branchId,
      updatedAt,
      operationId,
    },
  });
  if (error) {
    throw branchCommandError(
      error.message ?? '',
      error.message || 'No se pudo guardar la configuración.'
    );
  }
}

export async function updateBranchIdentity(branchId: string, values: BranchIdentityUpdate): Promise<void> {
  const name = values.name.trim();
  if (!name) {
    throw new Error('Ingresá un nombre para la sucursal.');
  }

  const updatedAt = new Date().toISOString();
  const addressPatch =
    values.address !== undefined ? { address: values.address?.trim() || null } : {};
  const payload: Record<string, unknown> = {
    branchId,
    name,
    updatedAt,
    ...addressPatch,
    ...(values.latitude !== undefined ? { latitude: values.latitude } : {}),
    ...(values.longitude !== undefined ? { longitude: values.longitude } : {}),
  };
  await applyBranchSettingsCommand(payload);
}

export async function updateBranchStockSalePolicy(branchId: string, policy: StockSalePolicy): Promise<void> {
  const updatedAt = new Date().toISOString();
  await applyBranchSettingsCommand({
    branchId,
    stockSalePolicy: policy,
    updatedAt,
  });
}

export async function updateBranchSellableModifierAssumption(
  branchId: string,
  assumption: 'base' | 'worst_case',
): Promise<void> {
  const updatedAt = new Date().toISOString();
  await applyBranchSettingsCommand({
    branchId,
    sellableModifierAssumption: assumption,
    updatedAt,
  });
}

export async function updateBranchBarcodeInputMode(branchId: string, mode: BranchBarcodeInputMode): Promise<void> {
  const updatedAt = new Date().toISOString();
  await applyBranchSettingsCommand({
    branchId,
    barcodeInputMode: mode,
    updatedAt,
  });
}

export async function updateBranchScaleBarcodeProfile(
  branchId: string,
  profile: BranchScaleBarcodeProfile
): Promise<void> {
  const updatedAt = new Date().toISOString();
  await applyBranchSettingsCommand({
    branchId,
    scaleBarcodeProfile: profile,
    updatedAt,
  });
}

export async function updateBranchManualDiscountThresholds(
  branchId: string,
  values: { manualDiscountMaxPercent: number; manualDiscountMaxAmount: number }
): Promise<void> {
  if (!Number.isFinite(values.manualDiscountMaxPercent) || values.manualDiscountMaxPercent <= 0) {
    throw new Error('El tope de porcentaje debe ser mayor a 0.');
  }
  if (!Number.isFinite(values.manualDiscountMaxAmount) || values.manualDiscountMaxAmount <= 0) {
    throw new Error('El tope en pesos debe ser mayor a 0.');
  }

  const updatedAt = new Date().toISOString();
  await applyBranchSettingsCommand({
    branchId,
    manualDiscountMaxPercent: values.manualDiscountMaxPercent,
    manualDiscountMaxAmount: values.manualDiscountMaxAmount,
    updatedAt,
  });
}

export async function updateBranchConsolidateCartLines(branchId: string, enabled: boolean): Promise<void> {
  const updatedAt = new Date().toISOString();
  await applyBranchSettingsCommand({
    branchId,
    consolidateCartLines: enabled,
    updatedAt,
  });
}

/** `branches.kitchen_fire_mode` — before_payment habilita "Mandar a cocina" en mostrador sin cobrar. */
export async function updateBranchKitchenFireMode(
  branchId: string,
  mode: 'after_payment' | 'before_payment',
): Promise<void> {
  const updatedAt = new Date().toISOString();
  await applyBranchSettingsCommand({
    branchId,
    kitchenFireMode: mode,
    updatedAt,
  });
}

/** Archiva comandas en listo cuando el pedido pasa a entregado. */
export async function updateBranchKitchenAutoArchiveOnDelivered(
  branchId: string,
  enabled: boolean,
): Promise<void> {
  const updatedAt = new Date().toISOString();
  await applyBranchSettingsCommand({
    branchId,
    kitchenAutoArchiveOnDelivered: enabled,
    updatedAt,
  });
}

export async function deactivateBranch(branchId: string): Promise<void> {
  const { error } = await supabase.rpc('deactivate_branch_for_my_organization', {
    p_branch_id: branchId,
  });
  if (error) {
    throw branchCommandError(error.message ?? '', 'No se pudo desactivar la sucursal.');
  }
}

export async function reactivateBranch(branchId: string): Promise<void> {
  const { error } = await supabase.rpc('reactivate_branch_for_my_organization', {
    p_branch_id: branchId,
  });
  if (error) {
    throw branchCommandError(error.message ?? '', 'No se pudo reactivar la sucursal.');
  }
}

export type BranchSummary = {
  id: string;
  name: string;
  location: string;
  latitude?: number | null;
  longitude?: number | null;
};

export async function fetchInactiveBranchesForOwner(): Promise<BranchSummary[]> {
  const userId = useAuthStore.getState().session?.user?.id;
  if (!userId) return [];

  const orgId = await resolveSessionOrganizationId(userId).catch((e: unknown) => {
    throw new Error(
      e instanceof Error && e.message
        ? e.message
        : 'No se pudo resolver la organización.',
    );
  });
  if (!orgId) return [];

  // Filtrar por org: la RLS authenticated también expone sucursales de tiendas públicas.
  const { data, error } = await supabase
    .from('branches')
    .select('id,name,address,latitude,longitude')
    .eq('organization_id', orgId)
    .eq('is_active', false)
    .order('name');
  if (error) throw new Error(error.message ?? 'No se pudieron cargar las sucursales inactivas.');
  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    location: row.address?.trim() || 'Dirección no cargada',
    latitude: typeof row.latitude === 'number' ? row.latitude : row.latitude ?? null,
    longitude: typeof row.longitude === 'number' ? row.longitude : row.longitude ?? null,
  }));
}
